import LogoutIcon from '@mui/icons-material/Logout'
import PersonIcon from '@mui/icons-material/Person'
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined'
import SettingsIcon from '@mui/icons-material/Settings'
import { Avatar, Box, Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useQueryClient } from '@tanstack/react-query'
import type { MouseEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { USER_ROLES } from '@/constants/userRoles'
import environment from '@/environment'
import { useAuth } from '@/features/authentication/contexts/AuthContext'

function getUserInitial(value: string | undefined): string {
  if (!value) return ''
  return value.trim().charAt(0).toUpperCase()
}

function NavBarUserMenu() {
  const { t } = useTranslation()
  const { logout, loggedInUser } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const hasAdminRole = loggedInUser?.role === USER_ROLES.ADMINISTRATORS.value
  const version = environment.deployedVersion

  function handleOpen(event: MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget)
  }

  function handleClose() {
    setAnchorEl(null)
  }

  function handleSettingsClick() {
    navigate('/profile')
    handleClose()
  }

  function handlePrivacyPolicyClick() {
    navigate('/privacy-policy')
    handleClose()
  }

  function handleLogoutClick() {
    queryClient.clear()
    logout()
    handleClose()
  }

  if (!loggedInUser) {
    return null
  }

  const initial = getUserInitial(loggedInUser.username ?? loggedInUser.email)

  return (
    <>
      <Tooltip arrow placement="bottom" title={t('user_profile.profile_menu.label')}>
        <IconButton
          aria-label={t('user_profile.profile_menu.label')}
          size="small"
          sx={(theme) => ({
            p: 0.25,
            borderRadius: '50%',
            transition: 'box-shadow 180ms cubic-bezier(0.16, 1, 0.3, 1)',
            boxShadow: open ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}` : 'none',
            '&:hover .NavBarUserMenu-avatar': {
              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}, 0 2px 4px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&:focus-visible .NavBarUserMenu-avatar': {
              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.5)}`,
            },
          })}
          onClick={handleOpen}
        >
          <Avatar
            className="NavBarUserMenu-avatar"
            sx={(theme) => ({
              width: 24,
              height: 24,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontSize: '0.75rem',
              fontWeight: 600,
              transition: 'box-shadow 180ms cubic-bezier(0.16, 1, 0.3, 1)',
              boxShadow: open ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}` : 'none',
            })}
          >
            {initial || <PersonIcon sx={{ fontSize: '0.875rem' }} />}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={open}
        slotProps={{
          paper: {
            elevation: 4,
            sx: {
              mt: 1,
              minWidth: 240,
              paddingY: 0.5,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={handleClose}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography
            noWrap
            sx={{
              color: 'text.primary',
              fontSize: '0.875rem',
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            {loggedInUser.username}
          </Typography>
          <Typography
            noWrap
            sx={{
              color: 'text.secondary',
              fontSize: '0.75rem',
              lineHeight: 1.4,
              mt: 0.25,
            }}
          >
            {loggedInUser.email}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />

        <MenuItem aria-label={t('user_profile.settings.title')} onClick={handleSettingsClick}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">{t('user_profile.settings.title')}</Typography>
        </MenuItem>
        <MenuItem aria-label={t('user_profile.privacy_policy.title')} onClick={handlePrivacyPolicyClick}>
          <ListItemIcon>
            <PrivacyTipOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">{t('user_profile.privacy_policy.title')}</Typography>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem aria-label={t('user_profile.sign_out.title')} onClick={handleLogoutClick}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">{t('user_profile.sign_out.title')}</Typography>
        </MenuItem>

        {hasAdminRole && version && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Typography
              sx={{
                px: 2,
                py: 0.5,
                fontSize: '0.6875rem',
                color: 'text.secondary',
                letterSpacing: 0.4,
              }}
              variant="body2"
            >
              {version}
            </Typography>
          </>
        )}
      </Menu>
    </>
  )
}

export default NavBarUserMenu
